/**
 * OrganizationQuickCreateDialog - Minimal dialog for quick company creation.
 *
 * Fields: Company query (name/website/domain → AI lookup), Name, Email, Relationship,
 * Organization Type. Redirects to detail page after creation for full editing.
 *
 * The Email field is optional and deliberately inside the quick-create budget: this
 * dialog is the inline create offered by the RFQ supplier picker, and an organization
 * with no address is one `Send` rejects (`RFQ_SUPPLIER_NO_EMAIL`) — so without it the
 * escape hatch manufactures exactly the state the guard refuses, and the user has to
 * leave the flow for Master Data to repair each org one at a time (measured in the
 * OC&CO M7 capstone: 4 vendors x ~6 interactions). It is the only field here that is
 * about being able to USE the record rather than about describing it. The column is a
 * set (`email_addresses TEXT[]`), so one address is the first element and the detail
 * page owns the rest.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Sparkles } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { CompanyLookupInput, DialogForm } from '../../../../shared/components/common';
import {
  CustomFieldsSection,
  activeCustomFieldDefinitions,
  relationshipScopeTokens,
  useCustomFieldDefinitions,
  validateCustomFields,
  visibleCustomFieldDefinitions,
  type CustomFieldErrors,
} from '../../../../shared/components/customFields';
import { useApiError } from '../../../../shared/hooks/useApiError';
import { getFieldErrors } from '../../../../shared/utils/errors';
import { useCreateOrganization } from '../hooks/useOrganizations';
import { useTiers } from '../hooks/useTiers';
import { duplicateCheckApi } from '../services/contactsApi';
import { useDuplicateCheck } from '../hooks/useDuplicateCheck';
import { DuplicateAlert } from './DuplicateAlert';
import { RelationshipChipPicker } from './RelationshipChipPicker';
import { resolveEnrichmentTierId, uploadEnrichmentLogo } from '../utils/enrichment';
import { navigateToEntity } from '../../../../shared/utils/entityUrls';
import type { Organization, OrganizationCreate, DomainEnrichmentResult } from '../types';
import classificationConfig from '../../../../shared-config/business_classification.config.json';

interface OrganizationQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Callback when company is created (before navigation) */
  onCreated?: (company: Organization) => void;
  /** If true, don't navigate to detail page after creation */
  skipNavigation?: boolean;
  /** Pre-select these company relationship types (e.g., ['customer'] or ['vendor']) */
  defaultRelationships?: string[];
  /** Pre-fill the lookup field (e.g., from picker search text) */
  defaultLookup?: string;
  /** Pre-fill the name field (e.g., from picker search text) */
  defaultName?: string;
}

/** How the backend names a custom-field rejection (`field="custom_fields.<key>"`). */
const CUSTOM_FIELD_ERROR_PREFIX = 'custom_fields.';

/**
 * The PAYLOAD field the email input feeds, and therefore the name a backend
 * rejection of it carries — `email_addresses`, or `email_addresses.0` once
 * Pydantic names the offending set member. The form registers `email`.
 */
const EMAIL_PAYLOAD_FIELD = 'email_addresses';

interface QuickCreateFormData {
  name: string;
  email: string;
  organization_relationship: string[];
  /** Tenant relationship-class ids — a separate stored dimension from the enum. */
  class_ids: string[];
  company_type: string;
  business_models?: string[];
  industry_verticals?: string[];
}

/** Mirrors the `format: "email"` the create schema declares for each set member. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function OrganizationQuickCreateDialog({
  open,
  onClose,
  onCreated,
  skipNavigation = false,
  defaultRelationships = [],
  defaultLookup = '',
  defaultName = '',
}: OrganizationQuickCreateDialogProps) {
  const { t } = useTranslation(['contacts', 'common']);
  const { t: tcf } = useTranslation('custom_fields');
  const navigate = useNavigate();
  const createMutation = useCreateOrganization();
  const { data: tiers } = useTiers();
  const { handleMutationError } = useApiError({ showToast: false });

  const [lookupValue, setLookupValue] = useState(defaultLookup);
  const [resolvedDomain, setResolvedDomain] = useState<string | null>(null);
  const enrichedFieldsRef = useRef<Partial<DomainEnrichmentResult>>({});

  // Progressive disclosure: start on the "Auto-fill from the web" choice card.
  // Reveal the manual fields once the user skips the lookup or a match enriches.
  // If we were seeded with a name (e.g. from picker search), skip straight to manual.
  const [mode, setMode] = useState<'choice' | 'manual'>(defaultName ? 'manual' : 'choice');
  const [hasMatch, setHasMatch] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onSubmit',
    defaultValues: {
      name: defaultName,
      email: '',
      organization_relationship: defaultRelationships,
      class_ids: [],
      company_type: 'corporation',
    },
  });

  const nameValue = watch('name');
  // Preflight the tiered duplicate check while typing, so a name/domain
  // collision surfaces here instead of only as the 409 the save is rejected with.
  const { match: duplicateMatch } = useDuplicateCheck(
    open ? { entity_type: 'organizations', name: nameValue || undefined } : null,
  );
  const companyType = watch('company_type');

  // Company custom fields, REQUIRED ones only. `POST /organizations` validates
  // `partial=False`, so a tenant's required field is not something the detail page
  // can collect afterwards — without it the create is refused, and this dialog is
  // the only create surface some entry points have. The optional ones stay off the
  // dialog: that is what keeps the quick-create budget honest
  // (.claude/rules/rules-ui-dialogs.md) while the mandatory ones are askable at all.
  //
  // A company's definitions are RELATIONSHIP-SCOPED, so which ones are required
  // depends on the relationships picked in this same dialog — mirror the backend's
  // `relationship_scope_tokens(data.organization_relationship, data.class_ids)`.
  // Both arms: a field scoped to a tenant relationship class is required the
  // moment that class is ticked in the same control.
  const organizationRelationship = watch('organization_relationship');
  const classIds = watch('class_ids');
  const cfRelationships = useMemo(
    () => relationshipScopeTokens(organizationRelationship, classIds),
    [organizationRelationship, classIds],
  );
  const [customFields, setCustomFields] = useState<Record<string, unknown>>({});
  const [customFieldErrors, setCustomFieldErrors] = useState<CustomFieldErrors>({});
  const { data: cfData } = useCustomFieldDefinitions('company');
  const requiredCfDefinitions = useMemo(
    () =>
      visibleCustomFieldDefinitions(
        activeCustomFieldDefinitions(cfData?.definitions),
        cfRelationships,
      ).filter((d) => d.required),
    [cfData?.definitions, cfRelationships],
  );

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      reset({
        name: defaultName,
        email: '',
        organization_relationship: defaultRelationships,
        class_ids: [],
        company_type: 'corporation',
      });
      setLookupValue(defaultLookup);
      setResolvedDomain(null);
      enrichedFieldsRef.current = {};
      setMode(defaultName ? 'manual' : 'choice');
      setHasMatch(false);
      setCustomFields({});
      setCustomFieldErrors({});
    }
    prevOpenRef.current = open;
  }, [open, reset, defaultRelationships, defaultLookup, defaultName]);

  const handleEnrichment = useCallback(
    (result: DomainEnrichmentResult, domain: string) => {
      setResolvedDomain(domain);
      setHasMatch(true);
      setMode('manual');
      if (result.name && !nameValue) {
        setValue('name', result.name, { shouldValidate: true });
      }
      if (result.business_models?.length) {
        setValue('business_models', result.business_models);
      }
      if (result.industry_verticals?.length) {
        setValue('industry_verticals', result.industry_verticals);
      }
      enrichedFieldsRef.current = {
        industry: result.industry,
        employee_count: result.employee_count,
        description: result.description,
        website: result.website,
        linkedin_url: result.linkedin_url,
        tier_name: result.tier_name,
        country: result.country,
        city: result.city,
        logo_url: result.logo_url,
      };
    },
    [nameValue, setValue],
  );

  const preLookupCheck = useCallback(
    async (query: string): Promise<string | null> => {
      // Only check duplicates when the input is a clear domain-shaped string.
      const looksLikeDomain = /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(query.trim());
      if (!looksLikeDomain) return null;
      try {
        const match = await duplicateCheckApi.check({
          entity_type: 'organizations',
          domain: query.trim(),
        });
        if (match) {
          // entity_label is nullable (unnamed record, or one the caller may not
          // see) — interpolating it raw renders the literal word "null".
          return t('organizations.domainInUse', {
            companyName:
              match.entity_label?.trim() ||
              (match.entity_id ? t('duplicates.unnamedRecord') : t('duplicates.hiddenRecord')),
          });
        }
      } catch {
        // Non-actionable: skip duplicate warning if the check fails.
      }
      return null;
    },
    [t],
  );

  // `handleMutationError` files every field error the envelope names, and an
  // error filed on a name this form never registered is never cleared by
  // validating the ones it did — so `handleSubmit` refuses every LATER submit,
  // and the user fixes the field and Create does nothing for the rest of the
  // dialog's life. Two names can reach us that way:
  //
  //  * a custom-field rejection (`custom_fields.<key>`), dropped here and
  //    rendered by `CustomFieldsSection` instead — see the catch block below;
  //  * a rejection of the email, which the backend names for the PAYLOAD field
  //    (`email_addresses`, or `email_addresses.0` once Pydantic names the set
  //    member) while the input is registered as `email`. That one is REMAPPED
  //    rather than dropped: the message belongs on the input that produced it.
  const setNonCustomFieldError: typeof setError = (name, error, options) => {
    const field = String(name);
    if (field.startsWith(CUSTOM_FIELD_ERROR_PREFIX)) return;
    if (field === EMAIL_PAYLOAD_FIELD || field.startsWith(`${EMAIL_PAYLOAD_FIELD}.`)) {
      setError('email', error, options);
      return;
    }
    setError(name, error, options);
  };

  const onSubmit = async (data: QuickCreateFormData) => {
    const enriched = enrichedFieldsRef.current;

    // Before the logo upload, deliberately: a submit this refuses creates
    // nothing, so it must not leave an orphaned file behind either.
    const cfErrors = validateCustomFields(requiredCfDefinitions, customFields, tcf);
    setCustomFieldErrors(cfErrors);
    if (Object.keys(cfErrors).length > 0) return;

    const tierId = resolveEnrichmentTierId(enriched.tier_name, tiers);
    const logoFileId = enriched.logo_url
      ? await uploadEnrichmentLogo(enriched.logo_url, data.name)
      : undefined;

    // Omitted entirely when blank — the column is `TEXT[] NOT NULL DEFAULT '{}'`
    // and the schema defaults to `[]`, so sending `[""]` would store a blank
    // address that reads as reachable to every consumer of the set.
    const email = data.email.trim();

    const createData: OrganizationCreate = {
      name: data.name,
      ...(email ? { email_addresses: [email] } : {}),
      domain: resolvedDomain || undefined,
      organization_relationship: data.organization_relationship.length > 0 ? data.organization_relationship : undefined,
      class_ids: data.class_ids.length > 0 ? data.class_ids : undefined,
      company_type: data.company_type || undefined,
      business_models: data.business_models?.length ? data.business_models : undefined,
      industry_verticals: data.industry_verticals?.length ? data.industry_verticals : undefined,
      industry: enriched.industry || undefined,
      employee_count: enriched.employee_count || undefined,
      description: enriched.description || undefined,
      website: enriched.website || undefined,
      linkedin_url: enriched.linkedin_url || undefined,
      country: enriched.country || undefined,
      city: enriched.city || undefined,
      tier_id: tierId,
      logo_file_id: logoFileId,
      // A tenant with no required definitions in scope sends exactly the payload
      // it did before this dialog learned about custom fields.
      ...(requiredCfDefinitions.length > 0 ? { custom_fields: customFields } : {}),
    };

    try {
      const newCompany = await createMutation.mutateAsync(createData);
      onCreated?.(newCompany);
      onClose();

      if (!skipNavigation) {
        navigate(`/master-data/organizations/${newCompany.id}`);
      }
    } catch (error) {
      // A custom-field rejection names `custom_fields.<key>`, which is no field of
      // this form. Route those onto the inputs that own them — and keep them OUT
      // of react-hook-form: an error set on a name the form never registered is
      // never cleared by validating the fields it did, so `handleSubmit` refuses
      // every later submit. The user then fixes the field and Create does
      // nothing, for the rest of the dialog's life.
      const cfErrors: CustomFieldErrors = {};
      for (const [field, message] of Object.entries(getFieldErrors(error) ?? {})) {
        if (field.startsWith(CUSTOM_FIELD_ERROR_PREFIX)) {
          cfErrors[field.slice(CUSTOM_FIELD_ERROR_PREFIX.length)] = message;
        }
      }
      setCustomFieldErrors(cfErrors);
      handleMutationError(error, setNonCustomFieldError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('organizations.create')}
      onSubmit={mode === 'manual' ? handleSubmit(onSubmit) : undefined}
      submitLabel={t('common:app.create')}
      isLoading={isSubmitting}
      maxWidth="sm"
    >
      <div className="space-y-4">
        <DuplicateAlert match={duplicateMatch} entityType="organizations" />
        {(mode === 'choice' || hasMatch) && (
          <CompanyLookupInput
            framed
            value={lookupValue}
            onChange={setLookupValue}
            onEnrichment={handleEnrichment}
            preLookupCheck={preLookupCheck}
            onSkip={() => setMode('manual')}
            onSelectExisting={(organizationId) => {
              onClose();
              navigateToEntity(navigate, 'companies', organizationId);
            }}
            hint={t('organizations.autoFillSubtitle', 'Type a name, website, or domain and we\'ll find the {{organization}} for you.')}
          />
        )}

        {mode === 'manual' && !hasMatch && (
          <button
            type="button"
            onClick={() => setMode('choice')}
            className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border py-2 text-sm text-muted-foreground transition hover:border-primary hover:text-foreground"
          >
            <Sparkles className="h-4 w-4 text-primary" />
            {t('organizations.autoFillReopen', 'Auto-fill from the web')}
          </button>
        )}

        {mode === 'manual' && (
        <>
        <div className="space-y-2">
          <Label htmlFor="name" required>{t('organizations.name')}</Label>
          <Input
            {...register('name', { required: t('common:validation.required') })}
            id="name"
            className={cn(errors.name && 'border-destructive')}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="organization-email">{t('organizations.email')}</Label>
          <Input
            {...register('email', {
              validate: (value) =>
                !value.trim() || EMAIL_PATTERN.test(value.trim())
                  ? true
                  : t('common:validation.email'),
            })}
            id="organization-email"
            type="email"
            autoComplete="off"
            data-testid="organization-email"
            className={cn(errors.email && 'border-destructive')}
          />
          {errors.email ? (
            <p className="text-sm text-destructive">{errors.email.message}</p>
          ) : (
            <p className="text-xs text-muted-foreground">{t('organizations.emailHint')}</p>
          )}
        </div>

        {/* Both relationship vocabularies — the built-in enum and the tenant's own
            classes — in ONE control, because they answer one question about the
            record (WI 08ab4081). Two stored dimensions, hence two callbacks. */}
        <RelationshipChipPicker
          entity="organization"
          value={organizationRelationship || []}
          onChange={(next) => setValue('organization_relationship', next)}
          classValue={classIds || []}
          onClassChange={(next) => setValue('class_ids', next)}
        />

        <div className="space-y-2">
          <Label>{t('common:businessClassification.companyLegalType.label')}</Label>
          <Select
            value={companyType}
            onValueChange={(value) => setValue('company_type', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('common:businessClassification.companyLegalType.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {classificationConfig.company_legal_types.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {t(`common:businessClassification.companyLegalType.${type.value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Only the tenant's REQUIRED company custom fields, scoped to the
            relationships picked above — the create endpoint refuses without them,
            so they are part of the minimum this dialog owes; the optional ones
            stay on the detail page (.claude/rules/rules-ui-dialogs.md). */}
        {requiredCfDefinitions.length > 0 && (
          <CustomFieldsSection
            entityType="company"
            requiredOnly
            relationships={cfRelationships}
            values={customFields}
            onChange={setCustomFields}
            errors={customFieldErrors}
          />
        )}

        <p className="text-xs text-muted-foreground">
          {t('organizations.quickCreateHint')}
        </p>
        </>
        )}
      </div>
    </DialogForm>
  );
}
